import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { apiClient } from '../api/client';
import { useSocket } from '../hooks/useSocket';
import { useBoardUpdates } from '../hooks/useBoardUpdates';
import { colorForTechnician, initials } from '../utils/technicianColor';
import { TERMINAL_ORDER_STATUSES } from '../utils/orderFilters';
import type { GpsPosition, GpsTrail, Order } from '../types';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

type MarkerStatus = 'busy' | 'enroute' | 'overdue' | 'free';

const MARKER_STYLE: Record<MarkerStatus, { bg: string; border: string; color: string }> = {
  busy: { bg: '#1C3F94', border: '#1C3F94', color: '#fff' },
  overdue: { bg: '#fff', border: '#B3261E', color: '#B3261E' },
  enroute: { bg: '#fff', border: '#141A2A', color: '#141A2A' },
  free: { bg: '#fff', border: '#0E9384', color: '#0E9384' },
};

// Наряды конкретного сантехника определяют вид его точки на карте (как в макете —
// заполненный квадрат "в работе", просрочка/в пути/свободен — рамкой) — не просто
// цвет-по-id, а реальный рабочий статус прямо сейчас
function markerStatus(orders: Order[], technicianId: string): MarkerStatus {
  const mine = orders.filter((o) => o.technicianId === technicianId);
  const overdue = mine.some(
    (o) => o.scheduledAt && !TERMINAL_ORDER_STATUSES.includes(o.status) && new Date(o.scheduledAt).getTime() < Date.now(),
  );
  if (overdue) return 'overdue';
  if (mine.some((o) => o.status === 'IN_PROGRESS')) return 'busy';
  if (mine.some((o) => o.status === 'EN_ROUTE' || o.status === 'ARRIVED')) return 'enroute';
  return 'free';
}

// Имя сантехника всегда подписано рядом с точкой — раньше было видно только по клику
// (во всплывающей подсказке), теперь диспетчер сразу видит, где кто, без лишних кликов.
// Замолчавший телефон не пропадает с карты: точка остаётся на последнем месте за смену,
// но гаснет и подписывается «не на связи» (isOnline считает сервер, окно — 5 минут)
function technicianDivIcon(name: string, technicianId: string, status: MarkerStatus, isOnline: boolean): L.DivIcon {
  const s = MARKER_STYLE[status];
  const squareClass = isOnline ? 'technician-marker-square' : 'technician-marker-square technician-marker-square--offline';
  const badge = isOnline ? '' : '<span class="technician-marker-offline-badge">не на связи</span>';
  return L.divIcon({
    className: 'technician-marker',
    html: `<div class="${squareClass}" style="background:${s.bg};border-color:${s.border};color:${s.color}">${escapeHtml(initials(name))}</div><div class="technician-marker-label" style="border-left:3px solid ${colorForTechnician(technicianId)}">${escapeHtml(name)}${badge}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

const YEKATERINBURG_CENTER: [number, number] = [56.8389, 60.6057];

const overlaps = (a: DOMRect, b: DOMRect) => a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

// Подписи с именами не налезают друг на друга: если мастера рядом, подпись, которая легла бы
// поверх уже показанной или чужой точки, прячется — имя видно при наведении на точку.
// Пересчёт после каждого сдвига/масштаба карты и при обновлении точек
function LabelDeclutter({ version }: { version: unknown }) {
  const map = useMap();
  useEffect(() => {
    const run = () => {
      const root = map.getContainer();
      const labels = [...root.querySelectorAll<HTMLElement>('.technician-marker-label')];
      const squares = [...root.querySelectorAll<HTMLElement>('.technician-marker-square')];
      labels.forEach((l) => l.classList.remove('technician-marker-label--hidden'));
      const placed: DOMRect[] = [];
      for (const label of labels) {
        const rect = label.getBoundingClientRect();
        const own = label.previousElementSibling;
        const clash =
          placed.some((p) => overlaps(rect, p)) ||
          squares.some((sq) => sq !== own && overlaps(rect, sq.getBoundingClientRect()));
        if (clash) label.classList.add('technician-marker-label--hidden');
        else placed.push(rect);
      }
    };
    const frame = requestAnimationFrame(run);
    map.on('zoomend moveend resize', run);
    return () => {
      cancelAnimationFrame(frame);
      map.off('zoomend moveend resize', run);
    };
  }, [map, version]);
  return null;
}

function positionKey(p: { technicianId: string; deviceId: string | null }): string {
  return `${p.technicianId}:${p.deviceId ?? 'default'}`;
}

export default function LiveMap() {
  const [positions, setPositions] = useState<Record<string, GpsPosition>>({});
  const [trails, setTrails] = useState<GpsTrail[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  // Временно выключены по умолчанию — кнопка всё ещё позволяет включить вручную
  const [showTrails, setShowTrails] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [trailsError, setTrailsError] = useState<string | null>(null);
  // Живой ли сейчас сокет. От этого зависит, нужен ли резервный опрос позиций
  const [socketLive, setSocketLive] = useState(false);
  const [linkDown, setLinkDown] = useState(false);
  const socket = useSocket('tracking');

  function loadOrders() {
    apiClient
      .get<Order[]>('/orders', { params: { status: 'active' } })
      .then(({ data }) => setOrders(data))
      .catch((err) => console.error('Не удалось загрузить наряды для карты', err));
  }

  function loadPositions() {
    apiClient
      .get<GpsPosition[]>('/tracking/positions')
      .then(({ data }) => {
        const initial: Record<string, GpsPosition> = {};
        for (const p of data) {
          initial[positionKey(p)] = p;
        }
        setPositions(initial);
        setLoadError(null);
      })
      .catch((err) => {
        console.error('Не удалось загрузить позиции сантехников', err);
        setLoadError('Не удалось загрузить карту — попробуйте обновить страницу');
      });
  }

  function loadTrails() {
    apiClient
      .get<GpsTrail[]>('/tracking/trails')
      .then(({ data }) => {
        setTrails(data);
        setTrailsError(null);
      })
      .catch((err) => {
        console.error('Не удалось загрузить следы передвижения', err);
        setTrailsError('След передвижения не загрузился');
      });
  }

  useEffect(() => {
    loadPositions();
    loadTrails();
    loadOrders();
    // След не меняется каждую секунду — обновляем реже, чем точки на карте
    const interval = setInterval(loadTrails, 60000);
    // Статус точки (в работе/в пути/просрочка) зависит от нарядов — обновляем не так часто,
    // как позиции (те приходят по сокету), но достаточно, чтобы статус не протухал
    const ordersInterval = setInterval(loadOrders, 15000);
    // Точки перечитываем и при живом сокете: по сокету приходят только новые координаты,
    // а переход «был на связи» → «молчит 5 минут» сам по себе ничего не присылает
    const positionsInterval = setInterval(loadPositions, 30000);
    return () => {
      clearInterval(interval);
      clearInterval(ordersInterval);
      clearInterval(positionsInterval);
    };
  }, []);

  useEffect(() => {
    if (!socket) {
      setSocketLive(false);
      return;
    }
    const onPosition = (data: GpsPosition) => {
      setPositions((prev) => ({ ...prev, [positionKey(data)]: data }));
    };
    // После переподключения дочитываем позиции целиком: пока связи не было,
    // пуши потерялись, и в состоянии остались точки на момент обрыва
    const onConnect = () => {
      setSocketLive(true);
      loadPositions();
    };
    const onDisconnect = () => setSocketLive(false);
    socket.on('gps:position', onPosition);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    setSocketLive(socket.connected);
    return () => {
      socket.off('gps:position', onPosition);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, [socket]);

  // Пока сокет жив, точки приходят пушем и опрос не нужен. Если связь оборвалась,
  // карта раньше молча замирала: диспетчер видел позиции на момент обрыва и не знал,
  // что они устарели. Теперь на время обрыва включается опрос (WEB-10)
  useEffect(() => {
    if (socketLive) return;
    const id = setInterval(loadPositions, 15000);
    return () => clearInterval(id);
  }, [socketLive]);

  // Баннер не мигает на первых секундах загрузки страницы, пока сокет только подключается
  useEffect(() => {
    if (socketLive) {
      setLinkDown(false);
      return;
    }
    const id = setTimeout(() => setLinkDown(true), 5000);
    return () => clearTimeout(id);
  }, [socketLive]);

  useBoardUpdates(['orders'], loadOrders);

  const list = Object.values(positions);
  const devicesByTechnician = new Map<string, number>();
  for (const p of list) {
    devicesByTechnician.set(p.technicianId, (devicesByTechnician.get(p.technicianId) ?? 0) + 1);
  }

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <button
        className="map-trails-toggle"
        onClick={() => setShowTrails((v) => !v)}
        type="button"
      >
        {showTrails ? 'Скрыть след' : 'Показать след'}
      </button>
      <div className="map-notices">
        {loadError && <div className="map-load-error">{loadError}</div>}
        {linkDown && !loadError && (
          <div className="map-link-warning">Связь с сервером потеряна — позиции обновляются раз в 15 секунд</div>
        )}
        {showTrails && trailsError && <div className="map-load-error">{trailsError}</div>}
      </div>
      <MapContainer center={YEKATERINBURG_CENTER} zoom={12} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {showTrails &&
          trails.map((trail) => (
            <Polyline
              key={`${trail.technicianId}:${trail.deviceId ?? 'default'}`}
              positions={trail.points.map((p) => [p.lat, p.lng])}
              pathOptions={{ color: colorForTechnician(trail.technicianId), weight: 3, opacity: 0.7 }}
            />
          ))}
        {list.map((p) => {
          const deviceCount = devicesByTechnician.get(p.technicianId) ?? 1;
          const label = deviceCount > 1 && p.deviceId ? `${p.technicianName} (${p.deviceId})` : p.technicianName;
          const status = markerStatus(orders, p.technicianId);
          return (
            <Marker
              key={positionKey(p)}
              position={[p.lat, p.lng]}
              icon={technicianDivIcon(label, p.technicianId, status, p.isOnline)}
            >
              <Popup>
                <strong>{p.technicianName}</strong>
                {deviceCount > 1 && <div>Устройство: {p.deviceId ?? '—'}</div>}
                <br />
                {p.isOnline ? 'Обновлено' : 'Последний раз на связи'}:{' '}
                {new Date(p.recordedAt).toLocaleTimeString('ru-RU')}
              </Popup>
            </Marker>
          );
        })}
        <LabelDeclutter version={list} />
      </MapContainer>
      <div className="map-legend">
        <span className="map-legend-item"><span className="map-legend-swatch" style={{ background: MARKER_STYLE.busy.bg, borderColor: MARKER_STYLE.busy.border }} />в работе</span>
        <span className="map-legend-item"><span className="map-legend-swatch" style={{ borderColor: MARKER_STYLE.free.border }} />свободен</span>
        <span className="map-legend-item"><span className="map-legend-swatch" style={{ borderColor: MARKER_STYLE.overdue.border }} />просрочка</span>
        <span className="map-legend-item"><span className="map-legend-swatch" style={{ borderColor: MARKER_STYLE.enroute.border }} />в пути</span>
        <span className="map-legend-item"><span className="map-legend-swatch technician-marker-square--offline" style={{ borderColor: MARKER_STYLE.enroute.border }} />не на связи</span>
      </div>
    </div>
  );
}
